import React, { useState, useEffect } from 'react';
import { styled } from '@mui/system';
import { Container, FormDiv, ButtonCust } from "../styles/Product.style";
import { product_list, product_list_total, product_detail } from "../services/product";
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  TableContainer, Table, TableHead, IconButton, Box,
  TableRow, TableCell, TableBody, TablePagination, CircularProgress,
  Typography, TextField, Button, Collapse, Grid, Paper, Divider, Chip, Stack, Card, CardHeader, CardContent
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { BUTTON_STYLE, BUTTON_STYLE2 } from "../../../../constants/style";

const StyledContainer = styled(Container)({
  padding: '20px',
  marginTop: '20px',
});

const StyledTableContainer = styled(TableContainer)({
  maxHeight: 700,
  borderRadius: '8px',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
});

const CancelIconDIv = styled("div")({
  position: "relative",
});

const CancelFilterIcon = styled(CancelIcon)({
  position: "absolute",
  right: "-9px",
  zIndex: "1",
  top: "-14px",
  background: "white",
  borderRadius: "10px",
  cursor: "pointer"
});

type ProductRow = {
  productId: number;
  productName: string;
  categoryName: string;
  subCategoryName: string;
  createdDttm: number;
};

type ProductAttribute = {
  productAttributeId: number;
  companyId: number;
  companyProductCode: string;
  price: number;
  discount: number;
  finalPrice: number;
  qty: number;
  unitId: number;
  gst_percentage: number;
  landingPrice: number | null;
  productUnitCode?: string;
  unitName?: string;
  companyName?: string;     // if your API returns it
  company_name?: string;    // or this variant
};

type ProductDetail = {
  id: number;
  name: string;
  categoryId?: number;
  subCategoryId?: number;
  description?: string;
  storeId?: number;
  attributes?: ProductAttribute[];
};

// Trim trailing zeros like 5.000 -> 5, 5.5500 -> 5.55
const trimZeros = (val: number | string | null | undefined) => {
  if (val === null || val === undefined) return '';
  const s = typeof val === 'number' ? String(val) : val;
  if (!s.includes('.')) return s;
  return s.replace(/(\.\d*?[1-9])0+$|\.0+$/, '$1');
};

const ProductList: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [searchInput, setSearchInput] = useState<string>('');

  // row expand states
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [detailById, setDetailById] = useState<Record<number, ProductDetail | null>>({});
  const [detailLoading, setDetailLoading] = useState<Record<number, boolean>>({});
  const [detailError, setDetailError] = useState<Record<number, string | null>>({});

  useEffect(() => {
    fetchProductTotal();
  }, []);

  useEffect(() => {
    fetchProducts();
    // clear expand/detail caches when the page dataset changes
    setExpanded({});
    setDetailById({});
    setDetailLoading({});
    setDetailError({});
  }, [page, rowsPerPage]);

  const fetchProductTotal = async () => {
    fetchProductListTotal(searchInput);
  };

  const fetchProductListTotal = async (search: string) => {
    const response: any = await product_list_total(search);
    setTotalRows(response && response.data?.data && response.data?.data?.length > 0 ? response.data?.data[0].total : 0);
  };

  const fetchProducts = async () => {
    await fetchProductList(page + 1, rowsPerPage, searchInput);
  };

  const fetchProductList = async (pageNum: number, rows: number, search: string) => {
    setLoading(true);
    try {
      const response: any = await product_list(pageNum, rows, search);
      setProducts(response.data.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchProducts();
    fetchProductTotal();
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEdit = (productId: number) => {
    navigate(`/admin/product_edit/${productId}`);
  };

  const handleCreate = () => {
    navigate(`/admin/product`);
  };

  const clearFilter = async () => {
    setPage(0);
    setSearchInput("");
    await fetchProductList(1, rowsPerPage, '');
    await fetchProductListTotal("");
  };

  const fetchProductDetail = async (productId: number) => {
    setDetailLoading(prev => ({ ...prev, [productId]: true }));
    setDetailError(prev => ({ ...prev, [productId]: null }));
    try {
      const response: any = await product_detail(productId);
      const detail: ProductDetail =
        response?.data?.data ?? response?.data ?? response ?? null;
      setDetailById(prev => ({ ...prev, [productId]: detail }));
    } catch (e: any) {
      console.error('Error fetching product detail:', e);
      setDetailError(prev => ({ ...prev, [productId]: 'Failed to load product details.' }));
      setDetailById(prev => ({ ...prev, [productId]: null }));
    } finally {
      setDetailLoading(prev => ({ ...prev, [productId]: false }));
    }
  };

  const toggleExpand = (productId: number) => {
    setExpanded(prev => {
      const nextOpen = !prev[productId];
      // if opening and not yet loaded, fetch detail
      if (nextOpen && !detailById[productId] && !detailLoading[productId]) {
        fetchProductDetail(productId);
      }
      return { ...prev, [productId]: nextOpen };
    });
  };

  // NEW: Group by company and show only "qty unit_name : price"
  const renderCompanyCards = (attributes: NonNullable<ProductDetail['attributes']>) => {
    if (!attributes || attributes.length === 0) {
      return <Typography variant="body2" sx={{ px: 1.5, py: 1 }}>No attributes available.</Typography>;
    }

    // group by companyId
    const byCompany: Record<string, ProductAttribute[]> = attributes.reduce((acc, a) => {
      const key = String(a.companyId ?? 'unknown');
      (acc[key] = acc[key] || []).push(a);
      return acc;
    }, {} as Record<string, ProductAttribute[]>);

    return (
      <Grid container spacing={1.5}>
        {Object.entries(byCompany).map(([companyId, list]) => {
          // Try all common keys to find a company name if present
          const first = list[0];
          const companyName =
            first.companyName ||
            first.company_name ||
            (first.companyProductCode ? `Company #${companyId} • ${first.companyProductCode}` : `Company #${companyId}`);

          return (
            <Grid key={companyId} item xs={12} sm={6} md={4}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardHeader
                  title={
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {companyName}
                    </Typography>
                  }
                  sx={{ pb: 0.5 }}
                />
                <CardContent sx={{ pt: 1 }}>
                  <Stack spacing={0.75}>
                    {list.map((a) => {
                      const qty = trimZeros(a.qty);
                      const unit = a.unitName || a.productUnitCode || a.unitId || '';
                      const price = trimZeros(a.finalPrice ?? a.price);
                      return (
                        <Typography key={a.productAttributeId} variant="body2">
                          {qty} {unit} : ₹{price}
                        </Typography>
                      );
                    })}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <Container>
      <FormDiv>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h6">Product List</Typography>
          <div>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(`/admin/product_export`)}
              sx={BUTTON_STYLE2}
            >
              Export
            </Button> &nbsp;
            <ButtonCust onClick={handleCreate} sx={BUTTON_STYLE}>
              Add
            </ButtonCust>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(`/admin/product_import`)}
              sx={BUTTON_STYLE2}
            >
              Import
            </Button>
          </div>
        </Box>

        {/* Filters */}
        <Box sx={{ margin: '20px 0', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <TextField
            label="Search Product"
            variant="outlined"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            type="text"
            size="small"
            placeholder="Search your product"
          />
          <CancelIconDIv>
            {searchInput !== "" &&
              <CancelFilterIcon onClick={() => { clearFilter(); }} />
            }
            <Button variant="contained" color="primary" onClick={handleSearch}>
              Search
            </Button>
          </CancelIconDIv>
        </Box>

        <StyledContainer>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <StyledTableContainer>
              <Table stickyHeader aria-label="Product table">
                <TableHead>
                  <TableRow>
                    <TableCell width={52} />
                    <TableCell>No.</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Sub-Category</TableCell>
                    <TableCell>Created Date</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {products.map((product: ProductRow) => {
                    const isOpen = !!expanded[product.productId];
                    const detail = detailById[product.productId];
                    const isDetailLoading = !!detailLoading[product.productId];
                    const detailErr = detailError[product.productId];

                    return (
                      <React.Fragment key={product.productId}>
                        <TableRow hover>
                          <TableCell size="small">
                            <IconButton
                              size="small"
                              aria-label={isOpen ? "Collapse" : "Expand"}
                              onClick={() => toggleExpand(product.productId)}
                            >
                              {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell size="small">{product.productId}</TableCell>
                          <TableCell size="small">{product.productName}</TableCell>
                          <TableCell size="small">{product.categoryName}</TableCell>
                          <TableCell size="small">{product.subCategoryName}</TableCell>
                          <TableCell size="small">
                            {new Date(product.createdDttm).toLocaleDateString('en-GB')}
                          </TableCell>
                          <TableCell size="small">
                            <IconButton onClick={() => handleEdit(product.productId)}>
                              <EditIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>

                        <TableRow key={`detail-${product.productId}`}>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                              <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                                {isDetailLoading && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2">Loading product details…</Typography>
                                  </Box>
                                )}

                                {!isDetailLoading && detailErr && (
                                  <Typography variant="body2" color="error">{detailErr}</Typography>
                                )}

                                {!isDetailLoading && !detailErr && detail && (
                                  <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                      #{detail.id} — {detail.name}
                                    </Typography>

                                    <Divider sx={{ my: 1.5 }} />

                                    {renderCompanyCards(detail.attributes || [])}
                                  </Box>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>

              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={totalRows}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </StyledTableContainer>
          )}
        </StyledContainer>
      </FormDiv>
    </Container>
  );
};

export default ProductList;
